import { foldLegacyGear, goldFromLegacy, isLegacyResources } from "@highmarch/domain";
import { Clock, Logger, SaveStore } from "./ports";
import { SuspendedLevel, parseSuspended } from "./suspend";
import { parseBestStage } from "./runHistory";
import { SaveSections, parseSections, saveSections } from "./saveSections";

export const CURRENT_SAVE_SCHEMA = 7;

/** The local save, authoritative in v1 (ADR 0012: single-player and offline, no server record). v1: playerId, highestLevelCleared. v2: + attempts, timestamps. v3: + resources. v4: + profile (persistent levels, jobs; resources move into it). v5: + sections (feature-owned, saveSections.ts). v6: one resource, gold (wood/iron/crystal converted by value), and Valor merged into Marks (migrateV5ToV6); the top-level resources mirror is gone. v7: gear v2 — each item's level requirement and +N fold into an item level, the leftover +N gold refunded, and the profile gains its forge (migrateV6ToV7, docs/GEAR_V2.md). */
export interface SaveRecord {
  schemaVersion: number;
  playerId: string;
  highestLevelCleared: number;
  attempts: Record<string, number>;
  createdAt: number;
  updatedAt: number;
  /** Persistent progression (PlayerProfile JSON; its `gold` is the resource). */
  profile: unknown;
  /** Tutorial progress (P1.7.3): highest step completed; -1 = skipped/finished. Optional so v4 saves stay valid. */
  tutorialStep?: number;
  /** Tower ids the player has seen in the hub's Towers page; an owned tower not listed here wears a NEW badge. Absent on
   *  saves from before the badge: the hub then marks every tower owned at that moment as seen, so nothing old reads as new. */
  seenTowers?: string[];
  /** Live-event ids the player has looked at (the More tab's dot); absent means none seen. */
  seenEvents?: string[];
  /** The hub's coached first upgrade (web ui/hub/coach.ts): index of the step reached; absent = not started. */
  coachStep?: number;
  /** Star conditions ever met per level (key: level number; value: a sim/stars.ts StarMask); absent on older saves. */
  stars?: Record<string, number>;
  /** Chapter star chests claimed per chapter (key: 1-based chapter; value: how many of its milestones). */
  starChests?: Record<string, number>;
  /** Daily Orders state (domain DailyOrders JSON); absent on older saves, validated by DailyOrders.fromJson. */
  orders?: unknown;
  /** Check-in calendar state (domain CheckIn JSON); absent on older saves, validated by CheckIn.fromJson. */
  checkin?: unknown;
  /** The battle put aside at a wave break or when the page was hidden (suspend.ts); absent when none is. */
  suspended?: SuspendedLevel;
  /** Best wave reached per level ("12": 14), for the defeat screen; absent on saves from before it. */
  bestStage?: Record<string, number>;
  /** Highest gear item id the player has seen on the Gear screen; items above it wear NEW (app gearView isNewGear). Absent
   *  on saves from before the badge: initGearSeen then marks everything held as seen, so nothing old reads as new. */
  gearSeenUpTo?: number;
  /** The Armoury's first-visit coach at the level gear becomes required has been shown (web armoury.ts). */
  gearGateSeen?: boolean;
  /** Captain Bram's guide (web ui/guide): absent on saves from before it; the web migrates tutorialStep/coachStep/gearGateSeen once. */
  guide?: GuideSaveRecord;
  /** Feature-owned sections by name (saveSections.ts `registerSaveSection`): wallet, market, events… Empty until a feature writes one. */
  sections: Record<string, unknown>;
}

export function newSave(playerId: string, now: number): SaveRecord {
  return { schemaVersion: CURRENT_SAVE_SCHEMA, playerId, highestLevelCleared: 0, attempts: {}, createdAt: now, updatedAt: now, profile: undefined, tutorialStep: 0, sections: {} };
}

export interface Migration { fromVersion: number; apply(doc: any, nowSeconds: number): any }

export class SaveMigrationError extends Error {}

const migrateV1ToV2: Migration = {
  fromVersion: 1,
  apply: (doc, now) => ({ schemaVersion: 2, playerId: doc.playerId, highestLevelCleared: doc.highestLevelCleared, createdAt: now, updatedAt: now, attempts: {} }),
};

const migrateV2ToV3: Migration = {
  fromVersion: 2,
  apply: (doc) => ({ ...doc, schemaVersion: 3, resources: { wood: 0, iron: 0, crystal: 0 } }),
};

/** v3 → v4: resources move into the profile; nothing else changes. */
const migrateV3ToV4: Migration = {
  fromVersion: 3,
  apply: (doc) => ({ ...doc, schemaVersion: 4, profile: { resources: doc.resources ?? { wood: 0, iron: 0, crystal: 0 } } }),
};

/** v4 → v5: an empty map for the feature-owned sections (saveSections.ts); nothing else changes. */
const migrateV4ToV5: Migration = {
  fromVersion: 4,
  apply: (doc) => ({ ...doc, schemaVersion: 5, sections: {} }),
};

/**
 * The shops.json rates Valor was priced at when it merged into Marks (2026-09-30): frozen here, as a migration must be,
 * so a later re-tune of the live rates never changes what an old save converts to.
 */
const VALOR_MERGE = { marksPerGem: 30, marksLevelDivisor: 50, valorPerGem: 2 } as const;

/**
 * Marks for `valor` at highest cleared `level`: its gem value (valor / valorPerGem) at the Marks rate of that level
 * (meta/shops.ts marksForGems), the rate the Event shop now prices at, so a saved balance buys what it bought.
 */
export function marksFromValor(valor: number, level: number): number {
  const r = VALOR_MERGE, L = Math.max(1, level);
  return Math.floor((valor * r.marksPerGem * (r.marksLevelDivisor + L)) / (r.marksLevelDivisor * r.valorPerGem));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;
const isObject = (v: unknown): v is Record<string, Json> => typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * v5 → v6 (owner decisions 2026-09-30): wood, iron and crystal become gold and Valor becomes Marks, everywhere a save
 * holds them — the profile's balance, each gear item's upgrade spend, each queued upgrade's paid cost, each Daily Order's
 * reward, the wallet's Valor and its Valor ledger lines. Gold by value (domain meta/gold.ts goldFromLegacy), Marks at
 * Valor's buying power (`marksFromValor`). The top-level `resources` mirror of v3 is dropped (the profile holds it since
 * v4). Anything already in the new shape is left as it is.
 */
const migrateV5ToV6: Migration = {
  fromVersion: 5,
  apply: (doc) => {
    const { resources, ...rest } = doc;
    const out: Json = { ...rest, schemaVersion: 6 };
    const gold = (v: unknown): unknown => (isLegacyResources(v) ? goldFromLegacy(v) : v);
    if (isObject(doc.profile)) {
      const p: Json = { ...doc.profile };
      if (isLegacyResources(p.resources)) { p.gold = goldFromLegacy(p.resources); delete p.resources; }
      else if (p.gold === undefined && isLegacyResources(resources)) p.gold = goldFromLegacy(resources);
      if (Array.isArray(p.gear)) p.gear = p.gear.map((g: Json) => (isObject(g) ? { ...g, upgradeSpent: gold(g.upgradeSpent) } : g));
      if (Array.isArray(p.queue)) p.queue = p.queue.map((q: Json) => (isObject(q) ? { ...q, cost: gold(q.cost) } : q));
      out.profile = p;
    }
    if (isObject(doc.orders) && Array.isArray(doc.orders.orders)) {
      out.orders = { ...doc.orders, orders: doc.orders.orders.map((o: Json) => {
        if (!isObject(o) || !isLegacyResources(o.reward)) return o;
        const { wood, iron, crystal, ...reward } = o.reward;
        return { ...o, reward: { ...reward, gold: goldFromLegacy({ wood, iron, crystal }) } };
      }) };
    }
    const wallet = isObject(doc.sections) ? doc.sections.wallet : undefined;
    if (isObject(wallet)) {
      const level = Number.isInteger(doc.highestLevelCleared) ? doc.highestLevelCleared : 0;
      const { valor, ...w } = wallet;
      const valorHeld = Number.isInteger(valor) && valor > 0 ? valor : 0;
      if (valorHeld > 0) w.marks = (Number.isInteger(w.marks) && w.marks > 0 ? w.marks : 0) + marksFromValor(valorHeld, level);
      if (Array.isArray(w.ledger)) {
        w.ledger = w.ledger.map((e: Json) => (isObject(e) && e.currency === "valor"
          ? { ...e, currency: "marks", delta: Math.sign(e.delta) * marksFromValor(Math.abs(Number(e.delta) || 0), level), reason: e.reason === "valor" ? "eventPlay" : e.reason }
          : e));
      }
      out.sections = { ...doc.sections, wallet: w };
    }
    return out;
  },
};

/**
 * v6 → v7 (gear v2, docs/GEAR_V2.md §Migration; Kevin 2026-10-05): every gear item's level requirement and +N fold into
 * one item level, never weaker (domain meta/gearFold.ts, frozen there): L = max(requirement, 10 + 5 × N). The gold of
 * +N steps the requirement already covered comes back as gold; the levels above the requirement count as raised with
 * essence (so salvage or a sale later refunds their share, as it refunded half the gold before). The profile gains an
 * empty forge (no essence or sigils yet). Anything already in the v2 shape is left as it is.
 */
const migrateV6ToV7: Migration = {
  fromVersion: 6,
  apply: (doc) => {
    const out: Json = { ...doc, schemaVersion: 7 };
    if (isObject(doc.profile)) {
      const p: Json = { ...doc.profile };
      let refund = 0;
      if (Array.isArray(p.gear)) p.gear = p.gear.map((g: Json) => {
        if (!isObject(g)) return g;
        const f = foldLegacyGear(g as never);
        refund += f.refundGold;
        return f.item;
      });
      const gold = Number.isFinite(p.gold) && p.gold >= 0 ? Math.floor(p.gold) : 0;
      p.gold = gold + refund;
      if (!isObject(p.forge)) p.forge = { essence: 0, sigils: [0, 0, 0, 0, 0, 0], fused: [0, 0, 0, 0, 0, 0], raisedLevels: 0 };
      out.profile = p;
    }
    return out;
  },
};

export class SaveMigrator {
  private readonly byVersion = new Map<number, Migration>();
  constructor(migrations: Migration[] = [migrateV1ToV2, migrateV2ToV3, migrateV3ToV4, migrateV4ToV5, migrateV5ToV6, migrateV6ToV7]) { for (const m of migrations) this.byVersion.set(m.fromVersion, m); }

  migrate(doc: any, nowSeconds: number): any {
    let version = doc?.schemaVersion;
    if (typeof version !== "number") throw new SaveMigrationError("save has no schemaVersion");
    if (version > CURRENT_SAVE_SCHEMA) throw new SaveMigrationError(`Save schemaVersion ${version} is newer than this build supports (${CURRENT_SAVE_SCHEMA})`);
    while (version < CURRENT_SAVE_SCHEMA) {
      const m = this.byVersion.get(version);
      if (!m) throw new SaveMigrationError(`No migration from save schemaVersion ${version}`);
      doc = m.apply(doc, nowSeconds);
      if (doc.schemaVersion !== version + 1) throw new SaveMigrationError(`Migration from ${version} produced ${doc.schemaVersion}`);
      version = doc.schemaVersion;
    }
    return doc;
  }
}

export type SaveLoadOutcome = "loaded" | "migrated" | "fresh" | "corruptReplaced";

/** Loads and stores the local save. A corrupt or unreadable save is backed up under a separate key and replaced, never silently discarded. */
export class SaveService {
  static readonly Key = "save";
  static readonly BackupKey = "save.corrupt-backup";

  constructor(private readonly store: SaveStore, private readonly clock: Clock, private readonly log: Logger, private readonly migrator = new SaveMigrator(),
              private readonly sections: SaveSections = saveSections) {}

  /** Settings → reset save: removes the save and its corrupt backup. */
  deleteAll(): void { this.store.delete(SaveService.Key); this.store.delete(SaveService.BackupKey); }

  /**
   * `trialLoad` opens the record the way the game will (web main.ts: LevelCatalog.fromSave) and throws on one it
   * cannot: a damaged gear item or section passes the top-level validation but bricked the boot. Such a save takes
   * the corrupt path too: backed up, replaced by a fresh one, reported as `corruptReplaced`.
   */
  load(newPlayerId: () => string, trialLoad?: (record: SaveRecord) => void): { record: SaveRecord; outcome: SaveLoadOutcome } {
    const json = this.store.load(SaveService.Key);
    if (json === null) return { record: newSave(newPlayerId(), this.clock.utcNowSeconds()), outcome: "fresh" };
    try {
      const doc = JSON.parse(json);
      const version = doc.schemaVersion;
      const migrated = this.migrator.migrate(doc, this.clock.utcNowSeconds());
      const record = validate(migrated);
      trialLoad?.(record);
      const outcome: SaveLoadOutcome = version === CURRENT_SAVE_SCHEMA ? "loaded" : "migrated";
      if (outcome === "migrated") this.save(record);
      return { record, outcome };
    } catch (err) {
      this.log.log("error", `Save unreadable (${(err as Error).message}); backing up and starting fresh`);
      this.store.save(SaveService.BackupKey, json);
      const record = newSave(newPlayerId(), this.clock.utcNowSeconds());
      this.save(record);
      return { record, outcome: "corruptReplaced" };
    }
  }

  /** Settings → Import save: a pasted save through the same migrate + validate path as load(); throws on anything unreadable. */
  parse(json: string): SaveRecord {
    return validate(this.migrator.migrate(JSON.parse(json), this.clock.utcNowSeconds()));
  }

  save(record: SaveRecord): void {
    record.schemaVersion = CURRENT_SAVE_SCHEMA;
    this.sections.flush(record);
    record.updatedAt = this.clock.utcNowSeconds();
    this.store.save(SaveService.Key, JSON.stringify(record));
  }
}

function validate(doc: any): SaveRecord {
  if (typeof doc.playerId !== "string" || typeof doc.highestLevelCleared !== "number" || typeof doc.attempts !== "object" || doc.attempts === null) {
    throw new SaveMigrationError("save record has invalid fields");
  }
  return { schemaVersion: doc.schemaVersion, playerId: doc.playerId, highestLevelCleared: doc.highestLevelCleared, attempts: { ...doc.attempts }, createdAt: Number(doc.createdAt ?? 0), updatedAt: Number(doc.updatedAt ?? 0), profile: doc.profile, tutorialStep: Number.isInteger(doc.tutorialStep) ? doc.tutorialStep : 0, seenTowers: stringList(doc.seenTowers), seenEvents: stringList(doc.seenEvents), coachStep: Number.isInteger(doc.coachStep) ? doc.coachStep : undefined, stars: numberMap(doc.stars), starChests: numberMap(doc.starChests), orders: doc.orders, checkin: doc.checkin, suspended: parseSuspended(doc.suspended), bestStage: parseBestStage(doc.bestStage), gearSeenUpTo: Number.isInteger(doc.gearSeenUpTo) && doc.gearSeenUpTo >= 0 ? doc.gearSeenUpTo : undefined, gearGateSeen: doc.gearGateSeen === true ? true : undefined, guide: parseGuide(doc.guide), sections: parseSections(doc.sections) };
}

/** A list of strings, or undefined when the field is missing or malformed (an old save). */
function stringList(v: unknown): string[] | undefined {
  return Array.isArray(v) && v.every((x) => typeof x === "string") ? [...v] : undefined;
}

/** A string → non-negative integer map, or undefined when the field is missing or malformed (an old save). */
function numberMap(v: unknown): Record<string, number> | undefined {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return undefined;
  const out: Record<string, number> = {};
  for (const [k, n] of Object.entries(v)) if (Number.isInteger(n) && (n as number) >= 0) out[k] = n as number;
  return out;
}

/** The web guide's progress (web ui/guide/guide.ts GuideSaveJson): step ids done, the tips toggle, the walkthrough skip. */
export interface GuideSaveRecord { v: number; done: string[]; tipsOff?: boolean; walkthroughSkipped?: boolean }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseGuide(v: any): GuideSaveRecord | undefined {
  if (typeof v !== "object" || v === null || !Number.isInteger(v.v) || !Array.isArray(v.done)) return undefined;
  return { v: v.v, done: v.done.filter((x: unknown) => typeof x === "string"), tipsOff: v.tipsOff === true ? true : undefined, walkthroughSkipped: v.walkthroughSkipped === true ? true : undefined };
}
